import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { attritionApi } from '../attritionApi';

/**
 * `/attrition` — and the retired `/attrition/requests` — land on the FIRST category's register.
 *
 * The sidebar lists one entry per category (product call, 31 Oct 2026), so there is no
 * all-categories page to land on; a bookmark still has to land somewhere, and the landing walk
 * sends a reader here because it cannot wait for the categories to load.
 */
export function AttritionLandingPage() {
  const [target, setTarget] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    attritionApi
      .categories(controller.signal)
      .then((categories) => {
        if (!controller.signal.aborted) setTarget(categories[0]?.id ?? null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the categories.', 0));
      });

    return () => controller.abort();
  }, []);

  if (target) return <Navigate to={`/attrition/categories/${target}`} replace />;

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Human Resources', 'Attrition']} />

      {error ? (
        <ErrorState message={error.message} correlationId={error.correlationId} />
      ) : target === null ? (
        // No category at all — the register is read through categories, so there is nothing to open yet.
        <EmptyState message="No attrition category yet. Categories are added under Attrition → Settings → Categories." />
      ) : (
        <div className="p-4">
          <Skeleton className="h-64 w-full" />
        </div>
      )}
    </div>
  );
}
